import { bytesPerSecond, type TransferSample } from '@krp/ui-kit'

import type { PhotoProcessing, PhotoRecognition, SearchResponse } from './types'

/**
 * Ход одного поиска по фото для экрана: снимок → сжатие на устройстве → загрузка → этапы на сервере (событиями потока).
 * Это временное состояние одного экрана, а не данные сервера: живёт в памяти вкладки до следующего поиска (как и отправленный снимок),
 * в Redux и в хранилище не попадает. Здесь НЕТ «обойдено N из M»: поиск идёт по индексам, и общего знаменателя у него нет.
 */
export const RUN_STATES = ['pending', 'running', 'done', 'failed', 'skipped'] as const
export type RunState = (typeof RUN_STATES)[number]

/** Состояние этапа из события сервера. Незнакомое слово читается как «пропущено»: этап не идёт и не считается выполненным. */
export function toRunState(value: string): RunState {
  if (value === 'running' || value === 'done' || value === 'failed') return value
  return 'skipped'
}
export type RunPhase = 'idle' | 'preparing' | 'uploading' | 'processing' | 'done' | 'failed' | 'cancelled'

/**
 * Этапы сервера в порядке показа (`Platform.Contracts.Search.PhotoSearchStages`). `prepare` («Подготовка поиска») в плане не бывает и приходит
 * только когда поиск действительно ждал «холодную» службу текста: показывается лишь тогда. `judge` («Проверка», RP-FB77 П4) — тоже вне плана:
 * приходит, только когда ответ спорный и сервер сверяет варианты проверяющей моделью.
 */
export const SERVER_STAGES = ['read', 'signature', 'visual', 'barcode', 'text', 'merge', 'catalog', 'judge', 'prepare'] as const
export type ServerStage = (typeof SERVER_STAGES)[number]

export interface TextQuery {
  query: string
  weight: number
  scope: string
  found: number
  elapsedMs: number
}

export interface RunStage {
  state: RunState
  /** Когда этап начался на этом устройстве (`Date.now()`): от него считается «прошло». */
  startedAt: number | null
  elapsedMs: number | null
  counts: Readonly<Record<string, number>>
  note: string | null
  queries: readonly TextQuery[]
  /** «Обычно» по медиане последних поисков сервера; `null` — замеров мало. */
  typicalMs: number | null
}

export interface RunUpload {
  state: RunState
  loaded: number
  total: number | null
  startedAt: number | null
  elapsedMs: number | null
  samples: readonly TransferSample[]
  bytesPerSecond: number | null
}

export interface SearchRun {
  id: number
  phase: RunPhase
  source: 'file' | 'camera' | null
  fileName: string | null
  fileBytes: number | null
  startedAt: number | null
  finishedAt: number | null
  compress: { state: RunState; elapsedMs: number | null; fromBytes: number | null; toBytes: number | null; startedAt: number | null }
  upload: RunUpload
  stages: Readonly<Record<ServerStage, RunStage>>
  recognized: PhotoRecognition | null
  /** Как сервер обработал кадр (RP-FB55): приходит вместе с итогом; до итога и у прежнего сервера — `null`. */
  processing: PhotoProcessing | null
  /** Контраст выдачи по фото 0…1 (около 0 — фото не различает кандидатов); `null` — не оценивался. */
  visualContrast: number | null
  correlationId: string | null
  /**
   * Предварительная выдача (событие `preliminary`): список кандидатов до сверки ИИ. Показывается сразу и живёт, пока идёт сверка; итог её заменяет, а сбой позднего
   * этапа не стирает. `null` — предварительной выдачи не было (ответ однозначный или сервер прежний).
   */
  preliminary: SearchResponse | null
  /** Предварительная выдача собрана без фото (по тексту и штрихкоду) и ещё уточняется по фото; ложь — она ждёт сверки ИИ или её нет. */
  preliminaryRefining: boolean
}

/** Сколько последних замеров загрузки хранится для скорости: окно сглаживает рывки сети. */
const UPLOAD_SAMPLES_KEPT = 30

const EMPTY_STAGE: RunStage = { state: 'pending', startedAt: null, elapsedMs: null, counts: {}, note: null, queries: [], typicalMs: null }

function blank(id: number): SearchRun {
  return {
    id,
    phase: 'idle',
    source: null,
    fileName: null,
    fileBytes: null,
    startedAt: null,
    finishedAt: null,
    compress: { state: 'pending', elapsedMs: null, fromBytes: null, toBytes: null, startedAt: null },
    upload: { state: 'pending', loaded: 0, total: null, startedAt: null, elapsedMs: null, samples: [], bytesPerSecond: null },
    stages: mapStages(() => EMPTY_STAGE),
    recognized: null,
    processing: null,
    visualContrast: null,
    correlationId: null,
    preliminary: null,
    preliminaryRefining: false,
  }
}

const isServerStage = (value: string): value is ServerStage => SERVER_STAGES.some((stage) => stage === value)

/** Этапы сервера по порядку `SERVER_STAGES`: значение каждого даёт `make`. Полный набор ключей проверяет компилятор. */
function mapStages(make: (stage: ServerStage) => RunStage): Record<ServerStage, RunStage> {
  return {
    read: make('read'),
    signature: make('signature'),
    visual: make('visual'),
    barcode: make('barcode'),
    text: make('text'),
    merge: make('merge'),
    catalog: make('catalog'),
    judge: make('judge'),
    prepare: make('prepare'),
  }
}

/** Событие потока `photo/stream` после разбора (`api/progressEvents.ts`). */
export type StageEvent =
  | { type: 'plan'; plan: readonly { stage: string; typicalMs: number | null }[]; correlationId: string | null }
  | {
      type: 'stage'
      stage: string
      state: string
      elapsedMs: number | null
      counts: Readonly<Record<string, number>>
      recognized: PhotoRecognition | null
      queries: readonly TextQuery[]
      note: string | null
    }

let run = blank(0)
const listeners = new Set<() => void>()
let clock: () => number = () => Date.now()

function set(next: SearchRun): void {
  run = next
  for (const listener of listeners) listener()
}

/** Для тестов: подмена часов и сброс. */
export function resetSearchRunForTests(now: () => number = () => Date.now()): void {
  clock = now
  run = blank(0)
  for (const listener of listeners) listener()
}

export const searchRun = {
  get: (): SearchRun => run,
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener)
    return () => listeners.delete(listener)
  },

  /** Снимок выбран или снят: начался новый поиск, идёт подготовка (кадр не перекодируется). */
  beginPrepare(source: 'file' | 'camera', fileName: string | null, fileBytes: number | null): void {
    const now = clock()
    set({
      ...blank(run.id + 1),
      phase: 'preparing',
      source,
      fileName,
      fileBytes,
      startedAt: now,
      compress: { state: 'running', elapsedMs: null, fromBytes: fileBytes, toBytes: null, startedAt: now },
    })
  },

  /** Подготовка закончена. Кадр не перекодируется (качество решает сервер), поэтому шаг всегда «пропущен»; `toBytes` — вес кадра. */
  endPrepare(info: { toBytes: number }): void {
    const now = clock()
    const started = run.compress.startedAt ?? now
    set({
      ...run,
      compress: {
        ...run.compress,
        state: 'skipped',
        elapsedMs: Math.max(0, now - started),
        toBytes: info.toBytes,
      },
    })
  },

  /** Начало отправки. Если подготовки не было (повтор «Найти»), её шаг — «пропущено». */
  beginSend(file: { name: string | null; size: number }): void {
    const now = clock()
    const fresh = run.phase === 'preparing' ? run : { ...blank(run.id + 1), source: run.source ?? 'file', startedAt: now, fileName: file.name, fileBytes: file.size }
    set({
      ...fresh,
      phase: 'uploading',
      compress: fresh.compress.state === 'pending' ? { ...fresh.compress, state: 'skipped', toBytes: file.size } : fresh.compress,
      upload: { ...fresh.upload, state: 'running', startedAt: now, loaded: 0, total: file.size, samples: [{ at: now, loaded: 0 }], bytesPerSecond: null },
    })
  },

  uploadProgress(loaded: number, total: number | null): void {
    const now = clock()
    const samples = [...run.upload.samples, { at: now, loaded }].slice(-UPLOAD_SAMPLES_KEPT)
    set({
      ...run,
      upload: { ...run.upload, loaded, total: total ?? run.upload.total, samples, bytesPerSecond: bytesPerSecond(samples) },
    })
  },

  /** Событие сервера. Первое же означает, что кадр дошёл: загрузка закончена. */
  apply(event: StageEvent): void {
    const now = clock()
    let next: SearchRun = run
    if (run.upload.state === 'running') {
      const total = run.upload.total
      next = {
        ...next,
        phase: 'processing',
        upload: { ...run.upload, state: 'done', loaded: total ?? run.upload.loaded, elapsedMs: Math.max(0, now - (run.upload.startedAt ?? now)) },
      }
    }
    if (event.type === 'plan') {
      const stages = { ...next.stages }
      for (const item of event.plan) if (isServerStage(item.stage)) stages[item.stage] = { ...stages[item.stage], typicalMs: item.typicalMs }
      set({ ...next, stages, correlationId: event.correlationId ?? next.correlationId })
      return
    }
    if (!isServerStage(event.stage)) {
      set(next)
      return
    }
    const before = next.stages[event.stage]
    const state = toRunState(event.state)
    const stage: RunStage = {
      ...before,
      state,
      startedAt: state === 'running' ? now : before.startedAt,
      elapsedMs: event.elapsedMs ?? before.elapsedMs,
      counts: event.counts,
      note: event.note,
      queries: event.queries,
    }
    set({ ...next, stages: { ...next.stages, [event.stage]: stage }, recognized: event.recognized ?? next.recognized })
  },

  /**
   * Предварительная выдача пришла: список кандидатов можно показывать, пока сервер уточняет его (`refining` — по фото) или сверяет похожие варианты. Следующая
   * предварительная выдача заменяет прежнюю целиком.
   */
  setPreliminary(result: SearchResponse, refining = false): void {
    set({ ...run, preliminary: result, preliminaryRefining: refining })
  },

  /** Обработка кадра и контраст из итога: раздел «Как обработано фото» внутри «Деталей поиска» (только администратору). */
  setOutcome(outcome: { processing: PhotoProcessing | null; visualContrast: number | null }): void {
    set({ ...run, processing: outcome.processing, visualContrast: outcome.visualContrast })
  },

  /** Итог: поиск закончился (успехом, сбоем или отменой). Идущие этапы закрываются тем же состоянием. */
  finish(phase: 'done' | 'failed' | 'cancelled'): void {
    const now = clock()
    const close = (state: RunState): RunState => (state === 'running' ? (phase === 'done' ? 'done' : 'failed') : state)
    set({
      ...run,
      phase,
      finishedAt: now,
      compress: { ...run.compress, state: close(run.compress.state) },
      upload: { ...run.upload, state: close(run.upload.state) },
      stages: mapStages((stage) => ({ ...run.stages[stage], state: close(run.stages[stage].state) })),
    })
  },
}
